import { sqWorkbenchStore } from '@/core/core.stores';
import { useFluxPaths } from '@/core/hooks/useFluxPaths.hook';
import type { DateFormatPreference } from '@/datetime/dateFormatPreference.utilities';

export interface DateTimeFormatPreferences {
  use24HourFormat: boolean;
  dateFormat: DateFormatPreference;
}

/**
 * Canonical accessor for the workbench date/time display preferences.
 *
 * The `dateFormat` preference is applied by mutating Moment's global `longDateFormat`
 * (see `applyDateFormatPreference`) — a side effect React can't observe. Any component that
 * renders a date therefore needs a store subscription purely to re-render when the preference
 * changes, even when it never reads the value. Owning that subscription here means a caller
 * cannot read `use24HourFormat` without also picking up the `dateFormat` re-render trigger, so
 * the two paths no longer have to be listed by hand at every call site (and can't be forgotten
 * in a new one, which silently stops dates from updating live).
 */
export function useDateTimeFormatPreferences(): DateTimeFormatPreferences {
  const { use24HourFormat, dateFormat } = useFluxPaths(sqWorkbenchStore, ['use24HourFormat', 'dateFormat']);
  return { use24HourFormat, dateFormat };
}
